import Link from 'found/Link';
import { DateTime, Duration } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Card, CardHeader, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import { formatDateTimeFull } from 'src/util/i18n/helpers';

/**
 * @param {object} props
 * @returns {React.ReactElement} - AdminPortfolioDetailsCard component
 */
function AdminPortfolioDetailsCard(props) {
  const intl = useIntl();

  if (!props?.portfolio) {
    return null;
  }

  const { portfolio } = props || {};
  const {
    active, uuid, createdAt, externalIdentifier, title, updatedAt,
  } = portfolio || {};
  const { start, finish } = active || {};

  const timeZone = APIConfig()?.DEFAULT_TIMEZONE;

  const startDate = DateTime.local({ zone: timeZone })
    .startOf('day')
    .minus(Duration.fromISO('P1M1D'));

  const portfolioStart = DateTime.fromSeconds(active.start, { zone: timeZone });
  // the default start and finish is the create date of the portfolio
  let dateRange = portfolioStart && `?start=${portfolioStart.toISODate()}&finish=${portfolioStart.toISODate()}`;
  //  Portfolio is created before the startDate
  //  so we can show the default month range (taken care at the routes/index.jsx)
  if (portfolioStart <= startDate) {
    dateRange = '';
  }

  return (
    <Card>
      <CardHeader>
        <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details.card_header.portfolio_details.label" defaultMessage="Portfolio details" />
      </CardHeader>
      <Table responsive>
        <tbody>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.links.label" defaultMessage="Links" />
            </th>
            <td>
              <Link to={`/portfolios/${uuid}${dateRange}`}>
                <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.customer_page.label" defaultMessage="Customer Page" />
              </Link>
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.id.label" defaultMessage="ID" />
            </th>
            <td>
              <UUID uuid={uuid} />
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.external_identifier.label" defaultMessage="External identifier" />
            </th>
            <td>{externalIdentifier}</td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.active_status.label" defaultMessage="Active status" />
            </th>
            <td>
              <BadgeActive start={start} finish={finish} />
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.title.label" defaultMessage="Title" />
            </th>
            <td>{title}</td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.start.label" defaultMessage="Start" />
            </th>
            <td>
              {start && formatDateTimeFull(intl, start)}
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.finish.label" defaultMessage="Finish" />
            </th>
            <td>
              {finish && formatDateTimeFull(intl, finish)}
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.created_at.label" defaultMessage="Created at" />
            </th>
            <td>
              {createdAt && formatDateTimeFull(intl, createdAt)}
            </td>
          </tr>
          <tr>
            <th scope="row" className="text-right">
              <FormattedMessage id="admin.admin_portfolio.admin_portfolio_show.portfolio_details_card.table.head.updated_at.label" defaultMessage="Updated at" />
            </th>
            <td>
              {updatedAt && formatDateTimeFull(intl, updatedAt)}
            </td>
          </tr>
        </tbody>
      </Table>
    </Card>
  );
}

AdminPortfolioDetailsCard.propTypes = {
  portfolio: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    externalIdentifier: PropTypes.string.isRequired,
    active: PropTypes.shape({
      start: PropTypes.number.isRequired,
      finish: PropTypes.number,
    }),
    createdAt: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    updatedAt: PropTypes.number.isRequired,
  }).isRequired,
};

export default AdminPortfolioDetailsCard;
